import styled from 'styled-components';
import Colours from '../../../design-system/colours';
import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { QuestionOption } from '../../../common/model/dto/survey/question-option';
import OvTextField from './OvTextField';
import breakpoints from '../../../design-system/breakpoints';
import OvCompactButton from './OvCompactButton';
import { useTranslation } from 'react-i18next';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';

export interface OvQuestionOptionGeneratorProps {
  questionOptions?: QuestionOption[];
  onChange: (options: QuestionOption[]) => void;
}

const OvQuestionOptionGenerator: FC<OvQuestionOptionGeneratorProps> = ({
  onChange,
  questionOptions,
}) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<QuestionOption[]>([]);

  useEffect(() => {
    if (questionOptions) {
      setOptions(questionOptions);
    }
  }, [questionOptions]);

  const changeOptionLabel = (
    index: number,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;

    setOptions((options) => {
      const updatedOptions = options.map((option, innerIndex) => {
        return index === innerIndex ? { ...option, label: value } : option;
      });

      onChange(updatedOptions);

      return updatedOptions;
    });
  };

  const addNewOption = () => {
    setOptions((options) => {
      const updateOptions = [
        ...options,
        { id: (options?.length + 1).toString(), label: '' },
      ];
      onChange(updateOptions);

      return updateOptions;
    });
  };

  const removeOption = (index: number) => {
    if (options?.length > 1) {
      setOptions((options) => {
        const updateOptions = options.filter(
          (option, innerIndex) => index !== innerIndex
        );
        onChange(updateOptions);

        return updateOptions;
      });
    }
  };

  return (
    <Container>
      {options.map((option, index) => (
        <OptionRow key={option.id}>
          <StyledTextField
            placeholder={t('common.addText')}
            value={option?.label}
            onChange={(event) => changeOptionLabel(index, event)}
          />

          <StyledIconButton
            size="large"
            onClick={() => removeOption(index)}
            color="inherit"
          >
            <DeleteIcon />
          </StyledIconButton>
        </OptionRow>
      ))}
      <OvCompactButton onClick={addNewOption} icon={<AddCircleIcon />}>
        {t('common.actions.addNewOption')}
      </OvCompactButton>
    </Container>
  );
};

export default OvQuestionOptionGenerator;

const Container = styled.div`
  padding-top: 1rem;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1rem;
`;

const StyledTextField = styled(OvTextField)`
  && {
    .MuiFormHelperText-root.Mui-error {
      background: ${Colours.OV_WHITE};
      margin: 0;
      padding: 0.25rem 0.75rem;
    }

    &.MuiFormControl-root {
      width: 100%;

      .MuiInputBase-root {
        width: 100%;
      }
    }

    @media (min-width: ${breakpoints.sm}) {
      max-width: 26rem;
    }
  }
`;

const StyledIconButton = styled(IconButton)`
  && {
    &.MuiButtonBase-root {
      padding: 0.75rem;
      border-radius: 50%;
    }
  }
`;
